import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { normaliseHost } from './tenant';

/**
 * What an address means, before anybody has signed in.
 *
 * The login screen has to show a school's name and badge to a stranger, and it used to get them by
 * querying `schools` for a matching domain. A query needs list permission, and list permission over
 * `schools` is a public directory of every customer -- the one thing most at odds with a school
 * being sold its own portal.
 *
 * So `schools` becomes signed-in-only (listing it, developer-only), and the public surface is one
 * document per school, read by id. A stranger who reads it learns what the door already says, and
 * has no way to ask which other doors exist. Cloud Functions keep it in step; nothing here writes.
 */
const fromLookup = async (label) => {
  try {
    const snap = await getDoc(doc(db, 'tenant_lookup', label));
    // The collection answered, and it holds nothing under this label.
    if (!snap.exists()) return null;
    const d = snap.data() || {};
    return {
      id: label,
      schoolId: d.schoolId || '',
      name: d.name || '',
      logoUrl: d.logoUrl || '',
      primaryColor: d.primaryColor || '',
      disabled: d.disabled === true,
    };
  } catch (e) {
    // Could not ask -- which is not the same as being told no.
    return undefined;
  }
};

/**
 * The way this worked before the lookup existed, kept as a fallback.
 *
 * Not belt-and-braces for its own sake: the lookup is filled by a Cloud Function, so between
 * deploying this code and that function having run, every school would have no entry -- and a
 * missing entry does not degrade gracefully, it signs the person back out the instant they log in.
 * That made the deploy order load-bearing, which is a bad thing for a login screen to depend on.
 *
 * It stops working of its own accord when the rules are tightened, and by then it is never reached:
 * a school with a lookup entry never gets here.
 */
const fromSchoolsQuery = async (label) => {
  try {
    // Two shapes, because the field changed meaning: it used to hold the bare label and now holds
    // the whole address. The second candidate is the address the visitor is at, which is what the
    // field stores today -- there is no way to derive it from the label alone.
    const host = normaliseHost(typeof window === 'undefined' ? '' : window.location.hostname);
    const candidates = host && host !== label ? [label, host] : [label];

    let schoolDoc = null;
    for (const value of candidates) {
      const snap = await getDocs(query(collection(db, 'schools'), where('domain', '==', value)));
      if (!snap.empty) { schoolDoc = snap.docs[0]; break; }
    }
    if (!schoolDoc) return null;

    const school = schoolDoc.data() || {};
    let branding = school.sch_basic || {};
    try {
      const settings = await getDoc(doc(db, 'schools', schoolDoc.id, 'metadata', 'system_settings'));
      if (settings.exists() && settings.data().branding) {
        branding = { ...branding, ...settings.data().branding };
      }
    } catch (e) {
      // The school has never opened Settings, or the read is denied. Its name is enough.
    }

    return {
      id: label,
      schoolId: schoolDoc.id,
      name: school.name || '',
      logoUrl: branding.logoUrl || '',
      primaryColor: branding.primaryColor || '',
      disabled: school.disabled === true,
    };
  } catch (e) {
    // Denied or unreachable. Once the rules are tightened this is the normal outcome for an
    // unauthenticated caller, which is why it must not read as "no such school".
    return undefined;
  }
};

/**
 * Three answers, not two.
 *
 * `null` means a source spoke and no school carries this label -- an address worth telling somebody
 * they got wrong. `undefined` means nothing could be reached, which says nothing about the school
 * and must never be treated as one being absent.
 */
export const lookupTenant = async (domain) => {
  const label = String(domain || '').trim().toLowerCase();
  if (!label) return null;

  const primary = await fromLookup(label);
  if (primary) return primary;

  const fallback = await fromSchoolsQuery(label);
  if (fallback) return fallback;

  // Absent only when nothing contradicts it. One source saying "no entry" is not the same as there
  // being no school: an entry that was never published says exactly that, and refusing on it locks
  // out everybody at a school that plainly exists. Corroborated absence refuses; a lone absence
  // reads as "could not tell", and the rules take it from there.
  return (primary === null && fallback === null) ? null : undefined;
};
